import { Link } from 'react-router-dom';
import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import {
  Phone, Users, Calendar, MessageSquare, BarChart3, Globe,
  ArrowRight, Check, Star, Scissors, Sparkles, Stethoscope,
  Wrench, Scale, Home, PawPrint, ChevronDown, X,
  Hexagon
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import AvaSphere from '../components/marketing/AvaSphere';
import IncomingCallCard from '../components/marketing/IncomingCallCard';
import Vapi from '@vapi-ai/web';
import axios from 'axios';

const ASSISTANT_ID = process.env.REACT_APP_VAPI_PUBLIC_ASSISTANT_ID || '';
const VAPI_PUBLIC_KEY = process.env.REACT_APP_VAPI_PUBLIC_KEY || '';
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const PUBLIC_AVA_NUMBER = process.env.REACT_APP_AVA_PUBLIC_NUMBER || '+1 (850) 318-3677';

const niches = [
  { name: 'Hair Salons', icon: Scissors },
  { name: 'Spas', icon: Sparkles },
  { name: 'Dental Offices', icon: Stethoscope },
  { name: 'HVAC', icon: Wrench },
  { name: 'Law Firms', icon: Scale },
  { name: 'Real Estate', icon: Home },
  { name: 'Vet Clinics', icon: PawPrint },
];

const features = [
  { title: 'AI Receptionist', desc: 'Your AI answers every call with natural conversation, 24/7.', icon: Phone },
  { title: 'Smart CRM', desc: 'Every lead tracked, scored, and organized in your pipeline.', icon: Users },
  { title: 'Booking Calendar', desc: 'Scheduling that syncs with Google & Outlook.', icon: Calendar },
  { title: 'SMS Campaigns', desc: 'Targeted messages and followups sent automatically.', icon: MessageSquare },
  { title: 'Call Analytics', desc: 'AI summaries, transcripts, and actionable insights.', icon: BarChart3 },
  { title: 'Niche Smart Intake', desc: 'Ava asks the right questions for your industry. Restaurants, clinics, salons, and more.', icon: Sparkles },
];

const PRICING_PLANS = [
  {
    id: 'payg',
    name: 'Pay As You Go',
    payg: true,
    perMin: '0.45',
    perSms: '0.05',
    description: 'No monthly fee. Only pay for what Ava actually uses.',
    features: [
      { label: 'No monthly commitment', included: true },
      { label: '$0.45 / voice minute', included: true },
      { label: '$0.05 / text message', included: true },
      { label: 'Top up credits anytime (from $20)', included: true },
      { label: '1 Ava number + booking + CRM', included: true },
      { label: 'Low-balance alerts (email, text & call)', included: true },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    monthly: 179.99,
    yearly: 150,            // effective monthly when billed annually (~2 months free)
    yearlyTotal: 1800,
    description: 'For solo operators going live with AI reception.',
    features: [
      { label: '1 location & 1 Ava number', included: true },
      { label: '250 minutes / month included', included: true },
      { label: '100 SMS / month included', included: true },
      { label: 'Appointment booking', included: true },
      { label: 'Lead capture & CRM', included: true },
      { label: 'Overage billed at $0.45/min', included: true },
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    monthly: 279.99,
    yearly: 233,
    yearlyTotal: 2800,
    badge: 'Most Popular',
    popular: true,
    description: 'For growing teams that want it fully automated.',
    features: [
      { label: '1 location & 1 Ava number', included: true },
      { label: '500 minutes / month included', included: true },
      { label: '250 SMS / month included', included: true },
      { label: 'Everything in Starter', included: true },
      { label: 'Appointment reminders (SMS)', included: true },
      { label: 'Automatic follow-up & re-engagement', included: true },
      { label: 'Analytics & insights', included: true },
    ],
  },
  {
    id: 'agency',
    name: 'Agency · White-label',
    monthly: 1497,
    yearly: 1247,
    yearlyTotal: 14970,
    description: 'White-label & multi-location for agencies.',
    features: [
      { label: 'Up to 5 locations & Ava numbers', included: true },
      { label: '2,000 minutes / month included', included: true },
      { label: '1,000 SMS / month included', included: true },
      { label: 'Everything in Growth', included: true },
      { label: 'White-label branding', included: true },
      { label: 'Priority support', included: true },
    ],
  },
];

const ENTERPRISE_INDUSTRIES = [
  'Restaurant', 'Medical/Clinic', 'Contractor', 'Hair & Beauty',
  'Legal', 'Real Estate', 'Automotive', 'Other',
];
const ENTERPRISE_LOCATIONS = ['1', '2-5', '6-15', '15+'];
const ENTERPRISE_VOLUMES = ['Under 100', '100-500', '500-2000', '2000+'];

const faqs = [
  { q: 'What industries does NextBell support?', a: 'Restaurants, medical and dental clinics, hair salons, general contractors, law firms, real estate teams, currency exchanges, and more. Ava adapts her intake script automatically to your industry, so callers always feel like they reached the right place.' },
  { q: 'How long does setup take?', a: 'Under 5 minutes. Sign up, and your AI receptionist goes live immediately with a dedicated phone number.' },
  { q: 'Can I customize what the AI says?', a: 'Absolutely. Customize the greeting, personality, and conversation flow.' },
  { q: 'What happens if the AI can\'t handle a call?', a: 'It gracefully collects the caller\'s info and schedules a callback. You get notified instantly.' },
  { q: 'Do I keep my existing phone number?', a: 'Yes. Forward your existing number to your NextBell AI number.' },
  { q: 'Is there a contract?', a: 'No contracts. Month to month. Cancel anytime, no fees.' },
  { q: 'What is Pay As You Go?', a: 'No monthly fee — you load credits and Ava draws them down at $0.45 per voice minute and $0.05 per text. You only pay for what she actually uses, and we warn you by email, text, and call before your balance runs low. Credits are non-refundable once added.' },
  { q: 'How does the free trial work?', a: 'Full access for 7 days. No charge until day 8. Cancel anytime.' },
  { q: 'What integrations do you support?', a: 'Google Calendar, Outlook, Calendly, and more.' },
  { q: 'Is my data secure?', a: 'Enterprise grade encryption, SOC 2 compliant, HIPAA ready options.' },
];

export default function HomePage() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [enterpriseOpen, setEnterpriseOpen] = useState(false);

  // === Talk-to-Ava call state ===========================================
  // The hero pill opens a small "Incoming call" card (NOT a fullscreen modal).
  // 2 ring tones play. After ~4s we kick off Vapi. As soon as Vapi's
  // `call-start` event fires we close the card — the page's own AvaSphere
  // takes over, pulsing with Ava's voice via setVolume/setSpeaking.
  const sphereRef = useRef(null);
  const vapiRef = useRef(null);
  const [callRinging, setCallRinging] = useState(false);
  const [callLive, setCallLive] = useState(false);
  const [callError, setCallError] = useState('');
  const callStartedAtRef = useRef(null);
  const cutoffTimerRef = useRef(null);
  const ringAllowedRef = useRef(300);

  const _clearCallTimers = () => {
    if (cutoffTimerRef.current) { clearTimeout(cutoffTimerRef.current); cutoffTimerRef.current = null; }
  };

  const _initVapi = () => {
    if (vapiRef.current) return vapiRef.current;
    const v = new Vapi(VAPI_PUBLIC_KEY);
    v.on('speech-start', () => sphereRef.current?.setSpeaking(true));
    v.on('speech-end', () => sphereRef.current?.setSpeaking(false));
    v.on('volume-level', (vol) => sphereRef.current?.setVolume(vol));
    v.on('call-start', () => {
      // Hand off from the ringing card to the page sphere.
      setCallRinging(false);
      setCallLive(true);
    });
    v.on('call-end', () => {
      _clearCallTimers();
      const used = callStartedAtRef.current ? (Date.now() - callStartedAtRef.current) / 1000 : 0;
      callStartedAtRef.current = null;
      sphereRef.current?.reset();
      setCallRinging(false);
      setCallLive(false);
      if (used > 0) {
        axios.post(`${API_BASE}/api/demo/end`, { used_seconds: Math.round(used) }).catch(() => {});
      }
    });
    v.on('error', (e) => {
      console.error('Vapi error:', e);
      _clearCallTimers();
      setCallError(e?.errorMsg || e?.message || 'Connection error');
      setCallRinging(false);
      setCallLive(false);
    });
    vapiRef.current = v;
    return v;
  };

  // Hero pill / sphere click → pre-warm mic, ask backend for the demo quota,
  // then open the ringing card. The card will call onAnswered after ~4s.
  const handleAvaToggle = async () => {
    setCallError('');
    if (callLive) { try { vapiRef.current?.stop(); } catch {}; return; }
    if (callRinging) return; // already ringing
    if (!VAPI_PUBLIC_KEY) { setCallError('Vapi public key not configured'); return; }

    // Fire mic permission early so the user has answered the browser prompt
    // before Vapi.start() needs it (kills the 10-sec lag).
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      s.getTracks().forEach(t => t.stop());
    } catch {}

    let allowedSec = 300;
    try {
      const { data } = await axios.post(`${API_BASE}/api/demo/check`, {});
      if (!data.allowed) {
        setCallError(data.message || "Daily demo limit reached. Try again in 24 hours.");
        return;
      }
      allowedSec = data.seconds_remaining || 300;
    } catch {}
    ringAllowedRef.current = allowedSec;
    setCallRinging(true);
  };

  // Called by IncomingCallCard after the 2-ring sequence completes.
  const _startVapiCall = async () => {
    try {
      callStartedAtRef.current = Date.now();
      const cap = Math.max(15, Math.min(ringAllowedRef.current || 300, 300));
      await _initVapi().start(ASSISTANT_ID, { maxDurationSeconds: cap });
      cutoffTimerRef.current = setTimeout(() => {
        try { vapiRef.current?.stop(); } catch {}
      }, cap * 1000);
    } catch (e) {
      callStartedAtRef.current = null;
      setCallError(e?.message || 'Failed to start call');
      setCallRinging(false);
    }
  };

  useEffect(() => {
    // Dev/preview convenience: ?ring=1 in the URL forces the ringing card open
    // so we can iterate on the visual without needing a working Vapi key.
    if (typeof window !== 'undefined' && window.location.search.includes('ring=1')) {
      setCallRinging(true);
    }
    return () => { try { vapiRef.current?.stop(); } catch {}; _clearCallTimers(); };
  }, []);

  return (
    <div className="min-h-screen bg-[#f9f8f4]">
      {/* Hero - Dark, Full Viewport Height, Centered */}
      <section className="relative min-h-screen flex flex-col hero-dark overflow-hidden">
        {/* Header inside hero */}
        <div className="w-full px-6 md:px-12 py-5 flex items-center justify-between relative z-10">
          <Link to="/" data-testid="logo-link" className="flex items-center group">
            <span className="font-heading font-semibold text-white text-lg tracking-tight">NextBell</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-300 hover:text-white transition-colors px-4 py-2">Log in</Link>
            <Link to="/signup" className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-full px-5 py-2.5 transition-all shadow-sm">Start free trial</Link>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-start relative z-10 pt-6 md:pt-2">
          {/* Sphere on top, responsive size */}
          <div className="relative cursor-pointer w-[320px] h-[320px] sm:w-[440px] sm:h-[440px] md:w-[440px] md:h-[440px] lg:w-[480px] lg:h-[480px] flex items-center justify-center" onClick={handleAvaToggle} data-testid="hero-ava-sphere">
            <AvaSphere ref={sphereRef} />
            <p className="absolute -top-4 left-1/2 -translate-x-1/2 text-[10px] tracking-[3px] whitespace-nowrap font-mono" style={{ color: 'rgba(100,160,255,0.4)' }}>
              {callLive ? 'AVA IS LISTENING…' : 'AVA, nextbell.ai'}
            </p>
          </div>

          {/* "Talk to Ava live" pill — opens the small incoming-call card. */}
          <button
            type="button"
            onClick={handleAvaToggle}
            data-testid="header-talk-to-ava-btn"
            className={`mt-6 sm:mt-8 inline-flex items-center gap-2 text-sm font-medium rounded-full px-5 py-2.5 transition-all backdrop-blur-sm border ${
              callLive
                ? 'bg-red-500/15 border-red-400/40 text-red-200 hover:bg-red-500/25'
                : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
            }`}
          >
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${callLive ? 'bg-red-400' : 'bg-blue-400'}`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${callLive ? 'bg-red-500' : 'bg-blue-500'}`}></span>
            </span>
            {callLive ? 'End call' : 'Talk to Ava live'}
          </button>
          {callError && (
            <p className="mt-3 text-xs text-red-300 max-w-md text-center px-4" data-testid="ava-call-error">{callError}</p>
          )}

          {/* Headline pushed further below */}
          <div className="max-w-4xl mx-auto px-6 text-center mt-12 md:mt-16 pb-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 data-testid="hero-headline" className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.1] mb-4">
                Your AI receptionist that never sleeps
              </h1>
              <p className="text-sm md:text-base text-slate-300 max-w-xl mx-auto mb-6 leading-relaxed">
                Answer every call, book every appointment, manage every lead, on autopilot. Built for service businesses that want to grow.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/signup" data-testid="hero-cta-primary" className="bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-full px-7 py-3 transition-all shadow-lg hover:shadow-xl flex items-center gap-2">
                  Start 7 day free trial <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <p className="text-xs text-slate-400 mt-4">
                No credit card required. Cancel anytime.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works: dark, zig-zag, Apple-style scroll reveal */}
      <HowItWorksSection />

      {/* Features: bento grid */}
      <section data-testid="features-section" className="py-20 md:py-28 bg-[#f4f3ef]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-3 block">Features</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium tracking-tight text-gray-900 max-w-2xl mx-auto leading-[1.1]">
              Everything you need to grow.
            </h2>
            <p className="text-gray-500 mt-4 max-w-md mx-auto text-sm">
              One AI receptionist, five background agents, zero missed calls.
            </p>
          </div>

          {/* Mobile: mirror desktop bento — hero card on top, 2-col mini cards below (md:hidden). */}
          <FeatureBentoMobile />

          {/* Bento: 12-col grid, hero card spans 6×2, others fill the rest (desktop only) */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-12 md:auto-rows-[170px] gap-4">
            {/* HERO: AI Receptionist (6 cols × 2 rows on md+) */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="md:col-span-6 md:row-span-2 relative overflow-hidden bg-gradient-to-br from-white to-blue-50/60 border border-blue-100 rounded-3xl p-6 md:p-8 hover:border-blue-300 transition-all group">
              {/* decorative pulse rings, top-right */}
              <div className="pointer-events-none absolute -top-8 -right-8 md:-top-10 md:-right-10 w-40 h-40 md:w-56 md:h-56 opacity-40 group-hover:opacity-60 transition-opacity">
                <div className="absolute inset-0 rounded-full border border-blue-200/60 animate-pulse" />
                <div className="absolute inset-4 md:inset-6 rounded-full border border-blue-300/50" />
                <div className="absolute inset-8 md:inset-12 rounded-full border border-blue-400/40" />
                <div className="absolute inset-14 md:inset-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-[0_0_40px_rgba(59,130,246,0.4)] flex items-center justify-center">
                  <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
              </div>

              <div className="relative">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3 md:mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                  Live 24/7
                </span>
                <h3 className="font-heading text-2xl md:text-3xl font-medium text-gray-900 mb-3 leading-tight max-w-[14rem] md:max-w-xs">
                  AI Receptionist that actually sounds human.
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed max-w-sm">
                  Natural conversation, niche aware intake, real time CRM sync. Ava picks up every call, even the 2am ones, and never makes your customer feel like they're talking to a bot.
                </p>
                <div className="flex items-end gap-1 mt-5 md:mt-6 h-7">
                  {[8, 14, 20, 26, 22, 14, 8, 18, 24, 14, 8, 16].map((h, i) => (
                    <span key={i} className="w-[3px] rounded-full bg-blue-500/70" style={{ height: `${h}px` }} />
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Right-top: Smart CRM */}
            <BentoCard
              icon={Users}
              title="Smart CRM"
              desc="Every lead tracked, scored, and organized in your pipeline."
              span="md:col-span-3"
              delay={0.1}
              fromX={30}
            />

            {/* Right-top: Niche-Smart Intake */}
            <BentoCard
              icon={Sparkles}
              title="Niche Smart Intake"
              desc="Restaurants, clinics, salons. Ava asks the right questions for your industry."
              span="md:col-span-3"
              delay={0.18}
              fromX={30}
            />

            {/* Bottom row: Calendar / SMS (top right of hero) and Analytics (full width below) */}
            <BentoCard
              icon={Calendar}
              title="Booking Calendar"
              desc="Ava books appointments straight into your calendar."
              span="md:col-span-3"
              delay={0.05}
              fromY={30}
            />
            <BentoCard
              icon={MessageSquare}
              title="SMS Campaigns"
              desc="Reminders, win backs, and followups sent automatically."
              span="md:col-span-3"
              delay={0.10}
              fromY={30}
            />
            <BentoCard
              icon={BarChart3}
              title="Call Analytics"
              desc="AI summaries, transcripts, actionable insights."
              span="md:col-span-6"
              delay={0.21}
              fromY={30}
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="pricing-section" className="py-20 md:py-28 bg-[#f9f8f4]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-3 block">Pricing</span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-gray-900 mb-3">Simple, transparent pricing</h2>
            <p className="text-gray-500 text-sm">7 day free trial on all plans. No credit card required.</p>
            <div className="flex items-center justify-center gap-3 mt-6">
              <span className={`text-sm ${!annual ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>Monthly</span>
              <button data-testid="pricing-toggle" onClick={() => setAnnual(!annual)} className={`relative w-11 h-6 rounded-full transition-colors ${annual ? 'bg-blue-600' : 'bg-gray-200'}`}>
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${annual ? 'left-6' : 'left-1'}`} />
              </button>
              <span className={`text-sm ${annual ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>Yearly</span>
              {annual && <span data-testid="yearly-save-badge" className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium">Save 2 months</span>}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-7xl mx-auto">
            {PRICING_PLANS.map((plan, i) => {
              const isEnterprise = !!plan.enterprise;
              const isPopular = !!plan.popular;
              const cardCls = isEnterprise
                ? 'bg-[#18181b] border-gray-800 text-white'
                : isPopular
                  ? 'bg-white border-blue-500 ring-1 ring-blue-500 shadow-lg shadow-blue-100/40 md:-translate-y-1'
                  : 'bg-white border-gray-100';
              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`pricing-card-${plan.id}`}
                  className={`relative rounded-2xl p-7 border flex flex-col ${cardCls}`}>
                  {plan.badge && (
                    <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-medium px-3 py-1 rounded-full ${isEnterprise ? 'bg-white text-gray-900' : 'bg-blue-600 text-white'}`}>
                      {plan.badge}
                    </div>
                  )}

                  <h3 className={`font-heading text-xl font-medium mb-1 ${isEnterprise ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                  <p className={`text-sm mb-5 min-h-[40px] ${isEnterprise ? 'text-gray-400' : 'text-gray-500'}`}>{plan.description}</p>

                  <div className="mb-5">
                    {plan.payg ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-heading font-semibold text-gray-900">${plan.perMin}</span>
                          <span className="text-gray-400 text-sm">/min</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">+ ${plan.perSms}/text · pay only for use</p>
                      </>
                    ) : annual ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-4xl font-heading font-semibold text-gray-900">${plan.yearly}</span>
                          <span className="text-gray-400 text-sm">/mo</span>
                          <span className="text-xs text-gray-400 line-through">${plan.monthly}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Billed annually (${plan.yearlyTotal.toLocaleString()}/yr)</p>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl font-heading font-semibold text-gray-900">${plan.monthly}</span>
                        <span className="text-gray-400 text-sm">/mo</span>
                      </>
                    )}
                  </div>

                  {isEnterprise ? (
                    <button
                      type="button"
                      onClick={() => setEnterpriseOpen(true)}
                      data-testid="pricing-cta-enterprise"
                      className="block w-full text-center rounded-full py-2.5 font-medium text-sm transition-all bg-white hover:bg-gray-100 text-gray-900">
                      Contact Sales
                    </button>
                  ) : (
                    <Link
                      to="/signup"
                      data-testid={`pricing-cta-${plan.id}`}
                      className={`block w-full text-center rounded-full py-2.5 font-medium text-sm transition-all ${isPopular ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-sm' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}>
                      Start Free Trial
                    </Link>
                  )}

                  <ul className="mt-6 space-y-2.5 flex-1">
                    {plan.features.map((feat, j) => (
                      <li key={j} className={`flex items-start gap-2 text-sm ${feat.included ? (isEnterprise ? 'text-gray-200' : 'text-gray-600') : 'text-gray-300'}`}>
                        {feat.included ? (
                          <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isEnterprise ? 'text-blue-400' : 'text-blue-600'}`} />
                        ) : (
                          <X className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-300" />
                        )}
                        <span className={feat.included ? '' : 'line-through'}>{feat.label}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-500 mt-10">
            Need multiple locations or custom call volume?{' '}
            <button
              type="button"
              onClick={() => setEnterpriseOpen(true)}
              data-testid="pricing-cta-enterprise"
              className="text-blue-600 font-medium hover:underline">
              Talk to sales about Enterprise →
            </button>
          </p>
        </div>
      </section>

      {/* Enterprise Contact Sales modal */}
      {enterpriseOpen && <EnterpriseModal onClose={() => setEnterpriseOpen(false)} />}
      <IncomingCallCard
        open={callRinging}
        phoneNumber={PUBLIC_AVA_NUMBER}
        onAnswered={_startVapiCall}
        onCancel={() => { setCallRinging(false); try { vapiRef.current?.stop(); } catch {} }}
      />

      {/* FAQ */}
      <section data-testid="faq-section" className="relative py-20 md:py-28 bg-black overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-30"
             style={{ background: 'radial-gradient(60% 50% at 50% 30%, rgba(37,99,235,0.18) 0%, rgba(0,0,0,0) 60%)' }} />
        <div className="relative max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-400 mb-3 block">FAQ</span>
            <h2 className="font-heading text-3xl md:text-4xl font-medium tracking-tight text-white">Common questions</h2>
          </div>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden">
                <button data-testid={`faq-${i}`} onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/[0.06] transition-colors">
                  <span className="text-white text-sm font-medium pr-4">{faq.q}</span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <div className="px-5 pb-5"><p className="text-slate-400 text-sm leading-relaxed">{faq.a}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Extra minutes callout — slim banner that matches the dark aesthetic */}
      <section data-testid="extra-minutes-callout" className="bg-black border-t border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left">
          <div className="w-9 h-9 rounded-full bg-blue-500/15 border border-white/10 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-blue-400" />
          </div>
          <p className="text-sm text-slate-300">
            <span className="text-white font-medium">Go over your minutes?</span>
            {' '}Overage is billed at just <strong className="text-white">$0.45/min</strong> — or switch to Pay As You Go and only pay for what Ava uses. No surprises.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 hero-dark">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-3">Ready to stop missing calls?</h2>
          <p className="text-slate-400 mb-7">Start your 7 day free trial. No credit card, no commitment.</p>
          <Link to="/signup" data-testid="final-cta-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-full px-7 py-3 transition-all shadow-lg">
            Get started free <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}


const ENTERPRISE_INPUT = "w-full bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-gray-900 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

function ModalField({ label, required, children }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
    </div>
  );
}

function EnterpriseModal({ onClose }) {
  const [form, setForm] = useState({
    name: '', business_name: '', email: '', phone: '',
    num_locations: '', industry: '', call_volume: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.business_name.trim() || !form.email.trim()) {
      setError('Name, business name, and email are required.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enterprise-inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Submission failed.');
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="enterprise-modal" className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <h3 className="font-heading text-2xl font-semibold text-gray-900">Let's build something together</h3>
            <p className="text-sm text-gray-500 mt-1">Tell us about your business and we'll craft a custom plan.</p>
          </div>
          <button type="button" onClick={onClose} data-testid="enterprise-modal-close" className="text-gray-400 hover:text-gray-700 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="px-6 pb-6 text-center" data-testid="enterprise-success">
            <div className="w-12 h-12 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-gray-900 font-medium mb-1">Thanks!</p>
            <p className="text-sm text-gray-500 mb-5">We'll be in touch within 24 hours.</p>
            <button type="button" onClick={onClose} className="bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-full px-5 py-2.5">Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-3">
            <ModalField label="Full Name" required>
              <input data-testid="ent-name" type="text" value={form.name} onChange={setField('name')} className={ENTERPRISE_INPUT} required />
            </ModalField>
            <ModalField label="Business Name" required>
              <input data-testid="ent-business-name" type="text" value={form.business_name} onChange={setField('business_name')} className={ENTERPRISE_INPUT} required />
            </ModalField>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Email" required>
                <input data-testid="ent-email" type="email" value={form.email} onChange={setField('email')} className={ENTERPRISE_INPUT} required />
              </ModalField>
              <ModalField label="Phone">
                <input data-testid="ent-phone" type="tel" value={form.phone} onChange={setField('phone')} className={ENTERPRISE_INPUT} />
              </ModalField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ModalField label="Number of Locations">
                <select data-testid="ent-locations" value={form.num_locations} onChange={setField('num_locations')} className={ENTERPRISE_INPUT}>
                  <option value="">Select…</option>
                  {ENTERPRISE_LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </ModalField>
              <ModalField label="Industry">
                <select data-testid="ent-industry" value={form.industry} onChange={setField('industry')} className={ENTERPRISE_INPUT}>
                  <option value="">Select…</option>
                  {ENTERPRISE_INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </ModalField>
            </div>
            <ModalField label="Monthly Call Volume">
              <select data-testid="ent-volume" value={form.call_volume} onChange={setField('call_volume')} className={ENTERPRISE_INPUT}>
                <option value="">Select…</option>
                {ENTERPRISE_VOLUMES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </ModalField>
            <ModalField label="Message">
              <textarea data-testid="ent-message" rows={3} value={form.message} onChange={setField('message')} className={ENTERPRISE_INPUT} placeholder="Anything else we should know?" />
            </ModalField>

            {error && <div data-testid="enterprise-error" className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-sm text-red-700">{error}</div>}

            <button type="submit" disabled={submitting} data-testid="enterprise-submit" className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium rounded-full py-3 text-sm">
              {submitting ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── How it works — dark zig-zag scroll-reveal section ───
const HOW_STEPS = [
  {
    number: '01',
    title: 'Sign up & pick your industry',
    body: 'Pick your niche, connect your number. Ava is live in 60 seconds. No engineers, no wiring up Twilio yourself.',
    visual: 'signup',
  },
  {
    number: '02',
    title: 'Ava answers every call, 24/7',
    body: 'Natural conversation, niche specific intake questions, never a missed lead. Even at 2am on a Sunday.',
    visual: 'ava',
  },
  {
    number: '03',
    title: 'Your CRM fills itself',
    body: 'Lead, transcript, booking, summary. Everything pushed to your dashboard in real time. Zero data entry.',
    visual: 'crm',
  },
  {
    number: '04',
    title: 'AI agents follow up automatically',
    body: 'SMS reminders 24h before each appointment, win back nudges to lapsed customers, satisfaction followups after every visit.',
    visual: 'sms',
  },
];

function HowItWorksSection() {
  return (
    <section data-testid="how-it-works-section" className="relative hero-dark overflow-hidden py-24 md:py-32">
      {/* faint blue radial glow behind the section to keep it from feeling flat */}
      <div className="pointer-events-none absolute inset-0 opacity-40"
           style={{ background: 'radial-gradient(60% 50% at 50% 30%, rgba(37,99,235,0.18) 0%, rgba(0,0,0,0) 60%)' }} />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Eyebrow + headline */}        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20 md:mb-28">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-blue-400 mb-4 block">How it works</span>
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white max-w-3xl mx-auto">
            Live in under a minute. Running on autopilot from there.
          </h2>
        </motion.div>

        {/* Zig-zag steps */}
        <div className="space-y-28 md:space-y-40">
          {HOW_STEPS.map((step, i) => (
            <HowStep key={step.number} step={step} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function HowStep({ step, reverse }) {
  const textVariants = {
    hidden: { opacity: 0, x: reverse ? 60 : -60 },
    show: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
  };
  const visualVariants = {
    hidden: { opacity: 0, scale: 0.92, y: 30 },
    show: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.05 } },
  };
  const numberVariants = {
    hidden: { opacity: 0, y: 14 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.0 } },
  };
  const headlineVariants = {
    hidden: { opacity: 0, y: 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] } },
  };
  const bodyVariants = {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.22 } },
  };

  return (
    <div data-testid={`how-step-${step.number}`} className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${reverse ? 'md:[direction:rtl]' : ''}`}>
      {/* Text column */}
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.35 }}
        variants={textVariants}
        className="md:[direction:ltr] relative">
        <motion.span variants={numberVariants}
          className="block font-heading font-semibold text-[120px] md:text-[180px] leading-none text-white/[0.06] select-none -mb-12 md:-mb-20">
          {step.number}
        </motion.span>
        <motion.h3 variants={headlineVariants}
          className="relative font-heading text-3xl md:text-5xl font-medium tracking-tight text-white mb-5 leading-[1.1]">
          {step.title}
        </motion.h3>
        <motion.p variants={bodyVariants}
          className="relative text-slate-400 text-base md:text-lg max-w-md leading-relaxed">
          {step.body}
        </motion.p>
      </motion.div>

      {/* Visual column */}
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.35 }}
        variants={visualVariants}
        className="md:[direction:ltr] flex items-center justify-center">
        <HowVisual kind={step.visual} />
      </motion.div>
    </div>
  );
}

function HowVisual({ kind }) {
  if (kind === 'signup') return <SignupMock />;
  if (kind === 'ava') return <AvaCallMock />;
  if (kind === 'crm') return <CRMCardMock />;
  if (kind === 'sms') return <SMSChatMock />;
  return null;
}

const _glassCard = "relative bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-[0_30px_80px_-30px_rgba(37,99,235,0.45)]";

function SignupMock() {
  return (
    <div className={`${_glassCard} w-full max-w-sm`}>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
        </div>
        <span className="text-white text-sm font-medium">Activate your AI</span>
      </div>
      <div className="space-y-3">
        <MockField label="Business" value="Your business name" />
        <MockField label="Industry" value="Choose your niche" pill />
        <MockField label="Phone" value="+1 (XXX) XXX-XXXX" mono />
      </div>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        className="mt-5 w-full bg-blue-600 text-white text-sm font-medium rounded-full py-2.5 flex items-center justify-center gap-2 cursor-default pointer-events-none">
        <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
        Activate Ava
      </button>
    </div>
  );
}

function MockField({ label, value, mono, pill }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</p>
      <div className={`bg-white/[0.05] border border-white/10 rounded-xl px-3 py-2 text-sm text-white ${mono ? 'font-mono' : ''}`}>
        {pill ? (
          <span className="inline-flex items-center gap-1.5 bg-blue-500/15 text-blue-300 text-xs px-2.5 py-0.5 rounded-full">
            <Scissors className="w-3 h-3" />
            {value}
          </span>
        ) : value}
      </div>
    </div>
  );
}

function AvaCallMock() {
  return (
    <div className={`${_glassCard} w-full max-w-sm flex flex-col items-center text-center`}>
      <div className="relative w-44 h-44 mb-5">
        {/* concentric rings */}
        <div className="absolute inset-0 rounded-full border border-blue-500/20 animate-pulse" />
        <div className="absolute inset-3 rounded-full border border-blue-500/30" />
        <div className="absolute inset-6 rounded-full border border-blue-500/40" />
        <div className="absolute inset-10 rounded-full bg-gradient-to-br from-blue-500 via-blue-600 to-purple-700 shadow-[0_0_60px_rgba(59,130,246,0.55)] flex items-center justify-center">
          <Phone className="w-9 h-9 text-white" />
        </div>
      </div>
      <p className="text-[10px] tracking-[3px] text-blue-300/70 font-mono mb-1">INCOMING CALL</p>
      <p className="text-white font-medium">+1 (XXX) XXX-XXXX</p>
      <p className="text-slate-400 text-xs mt-1">Ava picked up · 0:03</p>
      <div className="flex items-center justify-center gap-1 mt-4">
        {[6, 10, 14, 18, 14, 10, 6, 12, 16, 10, 6].map((h, i) => (
          <span key={i} className="w-[3px] rounded-full bg-blue-400/80" style={{ height: `${h}px` }} />
        ))}
      </div>
    </div>
  );
}

function CRMCardMock() {
  return (
    <div className={`${_glassCard} w-full max-w-md`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">New lead · just now</span>
        <span className="text-[10px] bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full">Hot</span>
      </div>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-semibold">
          NL
        </div>
        <div>
          <p className="text-white font-medium">New caller</p>
          <p className="text-slate-400 text-xs">+1 (XXX) XXX-XXXX · caller@…</p>
        </div>
      </div>
      <div className="space-y-2">
        <CRMRow icon={Calendar} label="Booked" value="Sat · 2:00 PM" />
        <CRMRow icon={Sparkles} label="Service" value="Service of choice" />
        <CRMRow icon={MessageSquare} label="Notes" value="Asked about pricing · prefers Saturdays" muted />
      </div>
    </div>
  );
}

function CRMRow({ icon: Icon, label, value, muted }) {
  return (
    <div className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2">
      <Icon className="w-3.5 h-3.5 text-blue-400 mt-1 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
        <p className={`text-sm ${muted ? 'text-slate-300' : 'text-white'} truncate`}>{value}</p>
      </div>
    </div>
  );
}

function SMSChatMock() {
  return (
    <div className={`${_glassCard} w-full max-w-sm`}>
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/5">
        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
          <MessageSquare className="w-4 h-4 text-blue-400" />
        </div>
        <div>
          <p className="text-white text-sm font-medium">Customer</p>
          <p className="text-[10px] text-slate-500">Yesterday · 3:42 PM</p>
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex justify-start">
          <div className="bg-white/[0.06] border border-white/10 text-slate-200 text-sm rounded-2xl rounded-bl-md px-3.5 py-2 max-w-[85%]">
            Hi! Reminder: your appointment is tomorrow at 2pm. Reply CANCEL to cancel. See you soon!
          </div>
        </div>
        <div className="flex justify-end">
          <div className="bg-blue-600 text-white text-sm rounded-2xl rounded-br-md px-3.5 py-2 max-w-[80%]">
            Yes, I'll be there!
          </div>
        </div>
        <div className="flex justify-start items-center gap-1.5 text-[11px] text-slate-500 pl-1">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
          Confirmed · added to calendar
        </div>
      </div>
    </div>
  );
}

function BentoCard({ icon: Icon, title, desc, span = '', delay = 0, fromX = 0, fromY = 20 }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: fromX, y: fromY }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`${span} bg-white border border-gray-100 rounded-3xl p-6 hover:border-blue-200 hover:shadow-md transition-all group flex flex-col`}>
      <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
        <Icon className="w-4 h-4 text-blue-600" />
      </div>
      <h3 className="font-heading text-base font-medium text-gray-900 mb-1.5">{title}</h3>
      <p className="text-gray-500 text-[13px] leading-relaxed">{desc}</p>
    </motion.div>
  );
}


/* ────────────────────────────────────────────────────────────────────
   Mobile FEATURES — mirror the desktop bento exactly (same colors,
   same card styles, same icons), just stacked for narrow screens.
   Hero "AI Receptionist" card on top, 5 small bento cards below in a
   2-col grid (last one full width). Looks 1:1 with the website vibe.
   ──────────────────────────────────────────────────────────────────── */
function FeatureBentoMobile() {
  const small = [
    { icon: Users, title: 'Smart CRM', desc: 'Every lead tracked, scored, and organized in your pipeline.' },
    { icon: Sparkles, title: 'Niche Smart Intake', desc: 'Restaurants, clinics, salons. Ava asks the right questions for your industry.' },
    { icon: Calendar, title: 'Booking Calendar', desc: 'Ava books appointments straight into your calendar.' },
    { icon: MessageSquare, title: 'SMS Campaigns', desc: 'Reminders, win backs, and followups sent automatically.' },
    { icon: BarChart3, title: 'Call Analytics', desc: 'AI summaries, transcripts, actionable insights.' },
  ];

  return (
    <div className="md:hidden flex flex-col gap-4">
      {/* HERO (mirrors desktop hero card) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden bg-gradient-to-br from-white to-blue-50/60 border border-blue-100 rounded-3xl p-6">
        <div className="pointer-events-none absolute -top-6 -right-6 w-36 h-36 opacity-50">
          <div className="absolute inset-0 rounded-full border border-blue-200/60 animate-pulse" />
          <div className="absolute inset-3 rounded-full border border-blue-300/50" />
          <div className="absolute inset-7 rounded-full border border-blue-400/40" />
          <div className="absolute inset-11 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 shadow-[0_0_28px_rgba(59,130,246,0.4)] flex items-center justify-center">
            <Phone className="w-4 h-4 text-white" />
          </div>
        </div>
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-blue-600 font-semibold mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
            Live 24/7
          </span>
          <h3 className="font-heading text-xl font-medium text-gray-900 mb-2.5 leading-tight max-w-[12rem]">
            AI Receptionist that actually sounds human.
          </h3>
          <p className="text-gray-600 text-[13px] leading-relaxed">
            Ava picks up every call, even the 2am ones. Natural conversation, niche aware intake, real time CRM sync.
          </p>
          <div className="flex items-end gap-1 mt-5 h-6">
            {[8, 14, 20, 26, 22, 14, 8, 18, 24, 14, 8, 16].map((h, i) => (
              <span key={i} className="w-[3px] rounded-full bg-blue-500/70" style={{ height: `${h}px` }} />
            ))}
          </div>
        </div>
      </motion.div>

      {/* 2-col mini bento (last card full width for balance) */}
      <div className="grid grid-cols-2 gap-3">
        {small.map((c, i) => (
          <motion.div
            key={c.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-30px' }}
            transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
            className="bg-white border border-gray-100 rounded-2xl p-4">
            <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-3">
              <c.icon className="w-4 h-4 text-blue-600" />
            </div>
            <h4 className="font-heading text-[15px] font-medium text-gray-900 mb-1 leading-tight">{c.title}</h4>
            <p className="text-gray-500 text-[12px] leading-relaxed">{c.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
   Apple "Pro chip" style mobile FEATURES.
   Each feature = full bleed dark hero card with a giant animated number/stat
   that counts up from 0 -> final value when scrolled into view.
   Deep gradient backgrounds (one per feature), white text, glowing accent.
   ──────────────────────────────────────────────────────────────────── */
function FeatureProChipMobile() {
  const cards = [
    {
      icon: Phone,
      bg: 'from-[#0a1230] via-[#0c1a4a] to-[#1c1640]',
      accent: 'from-blue-400 to-cyan-300',
      glow: 'bg-blue-500/20',
      stat: 0.8, suffix: 's', decimals: 1, prefix: '',
      kicker: 'Pickup time',
      title: 'Ava answers in under a second.',
      desc: 'Even at 2am. Even on Christmas. Every call gets a warm, on brand greeting and a real conversation.',
    },
    {
      icon: Users,
      bg: 'from-[#062019] via-[#0a3a2a] to-[#04201d]',
      accent: 'from-blue-300 to-cyan-200',
      glow: 'bg-blue-500/20',
      stat: 27, suffix: '%', decimals: 0, prefix: '+',
      kicker: 'More leads converted',
      title: 'Every caller becomes a scored lead.',
      desc: 'Hot leads ping you instantly. Cold ones get a polite follow up text. Your CRM stays clean without a finger lifted.',
    },
    {
      icon: Sparkles,
      bg: 'from-[#22130b] via-[#3a1d2a] to-[#1e0e2c]',
      accent: 'from-amber-300 to-pink-300',
      glow: 'bg-amber-500/20',
      stat: 12, suffix: '+', decimals: 0, prefix: '',
      kicker: 'Verticals built in',
      title: 'Trained for your industry.',
      desc: 'Salons, clinics, restaurants, contractors. Ava asks the right intake questions for your trade, the way you would.',
    },
    {
      icon: Calendar,
      bg: 'from-[#06182a] via-[#0a2c44] to-[#08203a]',
      accent: 'from-cyan-300 to-blue-300',
      glow: 'bg-cyan-500/20',
      stat: 35, suffix: '%', decimals: 0, prefix: '−',
      kicker: 'Fewer no shows',
      title: 'Books straight into your calendar.',
      desc: 'Conflict checks, 24 hour reminders, follow ups by SMS. Your schedule fills itself.',
    },
    {
      icon: MessageSquare,
      bg: 'from-[#1c0728] via-[#2c0e3e] to-[#170c2a]',
      accent: 'from-fuchsia-300 to-purple-300',
      glow: 'bg-fuchsia-500/20',
      stat: 30, suffix: 's', decimals: 0, prefix: '<',
      kicker: 'Reply time',
      title: 'Texts answered for you.',
      desc: 'Ava replies on the rules you set. Off limits topics get drafted for review. The rest goes out instantly.',
    },
    {
      icon: BarChart3,
      bg: 'from-[#1a0d09] via-[#2c1410] to-[#330e16]',
      accent: 'from-orange-300 to-rose-300',
      glow: 'bg-orange-500/20',
      stat: 100, suffix: '%', decimals: 0, prefix: '',
      kicker: 'Calls captured',
      title: 'AI summaries on every single call.',
      desc: 'Recording, transcript, sentiment, outcome. Tap a postcard to listen. Find every promise you made, instantly.',
    },
  ];

  return (
    <div className="md:hidden flex flex-col gap-3">
      {cards.map((c, i) => (
        <ProChipCard key={c.title} index={i} {...c} />
      ))}
    </div>
  );
}

function ProChipCard({ icon: Icon, bg, accent, glow, stat, suffix, decimals, prefix, kicker, title, desc, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const mv = useMotionValue(0);
  const spring = useSpring(mv, { duration: 1400, bounce: 0 });
  const display = useTransform(spring, (latest) =>
    `${prefix}${decimals ? latest.toFixed(decimals) : Math.round(latest)}${suffix}`
  );

  useEffect(() => {
    if (inView) mv.set(stat);
  }, [inView, stat, mv]);

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`relative overflow-hidden rounded-[28px] bg-gradient-to-br ${bg} p-7 min-h-[440px] flex flex-col justify-between`}
    >
      {/* Background glow blob */}
      <div className={`pointer-events-none absolute -top-28 -right-20 w-[320px] h-[320px] rounded-full ${glow} blur-3xl`} />
      <div className={`pointer-events-none absolute -bottom-32 -left-16 w-[280px] h-[280px] rounded-full ${glow} blur-3xl opacity-60`} />

      {/* Top row: icon glyph */}
      <div className="relative flex items-center justify-between">
        <div className={`w-11 h-11 rounded-2xl bg-white/[0.06] backdrop-blur-md border border-white/10 flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" strokeWidth={1.6} />
        </div>
        <span className="text-[10px] uppercase tracking-[0.24em] text-white/40 font-medium tabular-nums">
          {String(index + 1).padStart(2, '0')} / 06
        </span>
      </div>

      {/* The animated stat */}
      <div className="relative mt-8">
        <p className={`bg-clip-text text-transparent bg-gradient-to-r ${accent} font-heading font-semibold tracking-[-0.04em] leading-none`}
          style={{ fontSize: '88px' }}>
          <motion.span>{display}</motion.span>
        </p>
        <p className="text-[11px] uppercase tracking-[0.24em] text-white/50 font-medium mt-3">{kicker}</p>
      </div>

      {/* Headline + body */}
      <div className="relative mt-7">
        <h3 className="font-heading text-[22px] leading-[1.18] tracking-[-0.01em] text-white font-medium">
          {title}
        </h3>
        <p className="text-[13.5px] leading-relaxed text-white/65 mt-2.5">{desc}</p>
      </div>

      {/* Subtle bottom rule */}
      <div className="relative mt-6 h-px bg-gradient-to-r from-white/0 via-white/10 to-white/0" />
    </motion.article>
  );
}


/* ────────────────────────────────────────────────────────────────────
   Old simple stack (kept for reference, not currently rendered).
   ──────────────────────────────────────────────────────────────────── */
function FeatureStackMobile() {
  const cards = [
    { icon: Phone, title: 'AI Receptionist', desc: 'Ava answers every call, even at 2am. Natural, warm, niche aware.' },
    { icon: Users, title: 'Smart CRM', desc: 'Every caller becomes a scored lead. Hot leads get an instant text.' },
    { icon: Sparkles, title: 'Niche Smart Intake', desc: 'Restaurants, clinics, salons. Ava asks the right questions for your industry.' },
    { icon: Calendar, title: 'Booking Calendar', desc: 'Ava books appointments straight into your calendar.' },
    { icon: MessageSquare, title: 'SMS Replies', desc: 'Targeted messages and follow ups sent automatically by Ava.' },
    { icon: BarChart3, title: 'Call Analytics', desc: 'AI summaries, transcripts, and outcomes on every call.' },
  ];

  return (
    <div className="md:hidden grid grid-cols-1 gap-3">
      {cards.map((c, i) => (
        <motion.div
          key={c.title}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-30px' }}
          transition={{ duration: 0.45, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="bg-white border border-gray-100 rounded-3xl p-5 shadow-[0_2px_18px_-8px_rgba(0,0,0,0.06)]"
        >
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center mb-3">
            <c.icon className="w-4 h-4 text-blue-600" />
          </div>
          <h3 className="font-heading text-base font-medium text-gray-900 mb-1.5">{c.title}</h3>
          <p className="text-gray-500 text-[13px] leading-relaxed">{c.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
   Old subway journey (kept for reference, not currently rendered).
   ──────────────────────────────────────────────────────────────────── */
function FeatureJourneyMobile() {
  const stations = [
    {
      icon: Phone, label: 'Live 24/7',
      title: 'AI Receptionist',
      desc: 'Ava picks up every call, even at 2am. Natural conversation, niche aware intake, real time CRM sync.',
      stat: 'Avg pickup · 0.8s',
    },
    {
      icon: Users, label: 'Auto organized',
      title: 'Smart CRM',
      desc: 'Every caller becomes a scored lead. Hot leads get an instant SMS. Nothing slips through.',
      stat: '+27% lead conversion',
    },
    {
      icon: Sparkles, label: 'Industry tuned',
      title: 'Niche Smart Intake',
      desc: 'Restaurants, clinics, salons. Ava asks the right questions for your industry, the way you would.',
      stat: '12+ verticals built in',
    },
    {
      icon: Calendar, label: 'Booking',
      title: 'Booking Calendar',
      desc: 'Ava books straight into your calendar, dodges conflicts, sends 24 hour reminders by SMS.',
      stat: 'Fewer no shows',
    },
    {
      icon: MessageSquare, label: 'SMS Campaigns',
      title: 'SMS Replies',
      desc: 'Targeted messages and follow ups sent automatically. Ava drafts, you approve, or set rules and let her send.',
      stat: 'Under 30s reply time',
    },
    {
      icon: BarChart3, label: 'Insights',
      title: 'Call Analytics',
      desc: 'AI summaries, transcripts, and actionable insights on every call. Click a postcard to listen.',
      stat: 'AI summaries on every call',
    },
  ];

  return (
    <div className="md:hidden relative">
      {/* The glowing rail behind everything (centered on the 40px icon) */}
      <div className="absolute left-[19px] top-3 bottom-3 w-[2px] bg-gradient-to-b from-blue-200/0 via-blue-200 to-blue-200/0 rounded-full" />

      <ol className="relative space-y-6">
        {stations.map((s, i) => (
          <FeatureStation key={s.title} index={i} total={stations.length} {...s} />
        ))}
      </ol>

      <div className="text-center mt-10 mb-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.24em] font-semibold text-blue-600">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
          End of the line
        </span>
        <p className="text-gray-500 text-sm mt-2">All of it works out of the box. No setup engineers.</p>
      </div>
    </div>
  );
}

function FeatureStation({ index, total, icon: Icon, label, title, desc, stat }) {
  return (
    <motion.li
      initial={{ opacity: 0, x: 24 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.55, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="relative pl-14"
    >
      {/* Station node — matches desktop BentoCard icon style exactly */}
      <div className="absolute left-0 top-0">
        <div className="relative">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
            <Icon className="w-4 h-4 text-blue-600" />
          </div>
          {/* Tiny step counter */}
          <span className="absolute -bottom-1 -right-1 bg-white border border-gray-200 text-[9px] tabular-nums font-semibold text-gray-700 w-5 h-5 rounded-full flex items-center justify-center shadow-sm">
            {String(index + 1).padStart(2, '0')}
          </span>
        </div>
      </div>

      {/* Card */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-[0_2px_18px_-8px_rgba(0,0,0,0.08)]">
        <span className="inline-flex items-center text-[10px] uppercase tracking-[0.2em] font-semibold text-blue-600 mb-1.5">
          {label}
        </span>
        <h3 className="font-heading text-[17px] font-medium text-gray-900 leading-tight">{title}</h3>
        <p className="text-gray-500 text-[13px] leading-relaxed mt-1.5">{desc}</p>

        <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1">
          <span className="w-1 h-1 rounded-full bg-blue-500" /> {stat}
        </div>
      </div>

      {index < total - 1 && (
        <span className="absolute left-[20px] -bottom-3 w-1 h-1 rounded-full bg-blue-400/60" />
      )}
    </motion.li>
  );
}


